import { Context } from '@nuxt/types'
import Vue from 'vue'
import { Route } from 'vue-router'
import { getRouteOption, getMatchedComponents } from '../../../utils/nuxt-utils'

/**
 * 캐시 기능 제공
 */
export class Cache {
  private context: Context = null

  constructor(context: Context) {
    this.context = context
    this.init()
  }

  private init() {
    this.context.app.router.afterEach((to, from) => {
      this.applyConfigCache(to, from)
    })
  }

  private applyConfigCache(to: Route, from: Route) {
    if (!from?.name) {
      return
    }

    const matchedComponents = getMatchedComponents(from)
    if (!matchedComponents || matchedComponents.length < 1) {
      return
    }

    const matchedComponent = matchedComponents[matchedComponents.length - 1]
    const routerOption = getRouteOption(from)?.config?.cache
    let includeCache = routerOption?.includeCache === undefined ? true : routerOption?.includeCache
    let clearCache = routerOption?.clearCache === undefined ? false : routerOption?.clearCache

    if (typeof includeCache === 'function') {
      includeCache = includeCache.call(matchedComponent, to, from)
    }

    if (typeof clearCache === 'function') {
      clearCache = clearCache.call(matchedComponent, to, from)
    }

    // clear cache
    if (clearCache === true) {
      this.clear()
      this.context.$logger().info('clear cache')
    }
    // 캐시 포함하지 않을 경우
    else if (includeCache === false) {
      this.remove(key => {
        return key === from.path || key.startsWith(from.path + '?')
      }, false)
      this.context.$logger().info('remove cache', from)
    }
  }

  /**
   * 특정 조건의 컴포넌트 제거
   * @param fn 컴포넌트 제거 조건 판단 메소드
   * @param excludeActive 활성화 된 컴포넌트 제거 여부
   */
  async remove(fn: (key: string) => boolean, excludeActive = true) {
    const keepAliveComponents = this.findKeepAliveComponents()
    const removeComponents = []

    for (const keepAliveComponent of keepAliveComponents) {
      if (!excludeActive || (excludeActive && keepAliveComponent._inactive === true)) {
        if (fn(keepAliveComponent.$vnode.data.key)) {
          removeComponents.push(keepAliveComponent)
        }
      }
    }

    for (const vm of removeComponents) {
      try {
        await vm.$destroy()
      } catch (e) {}
    }
  }

  /**
   * 캐시된 컴포넌트를 모두 제거
   */
  async clear(excludeActive = true) {
    const keepAliveComponents = this.findKeepAliveComponents()
    const removeComponents = []
    for (const keepAliveComponent of keepAliveComponents) {
      if (!excludeActive || (excludeActive && keepAliveComponent._inactive === true)) {
        removeComponents.push(keepAliveComponent)
      }
    }
    for (const vm of removeComponents) {
      try {
        await vm.$destroy()
      } catch (e) {}
    }
  }

  /**
   * 캐시된 컴포넌트를 조회한다.
   */
  public findKeepAliveComponents() {
    const keepAliveParentComponents = this.findKeepAliveParentComponents()
    const keepAliveComponents = []

    for (const component of keepAliveParentComponents) {
      for (const vm of component.$children) {
        keepAliveComponents.push(vm)
      }
    }

    return keepAliveComponents
  }

  /**
   * 캐시 된 컴포넌트에 포함된 경우, inactive 상태인지 확인
   * @param compoenent 컴포넌트
   */
  public isInactivatedComponent(component: Vue) {
    while (component) {
      // @ts-ignore
      if (component._inactive === true) {
        return true
      }

      component = component.$parent
    }
    return false
  }

  /**
   * 캐시된 부모 컴포넌트를 조회한다.
   */
  private findKeepAliveParentComponents() {
    const keepAliveComponents = []
    const findComponent = (children: Vue[]) => {
      if (!children) {
        return
      }

      for (const child of children) {
        // @ts-ignore
        if (child.keepAlive === true) {
          keepAliveComponents.push(child)
        }

        findComponent(child.$children)
      }
    }

    findComponent(window.$nuxt.$children)

    return keepAliveComponents
  }
}
